import { useCallback, useSyncExternalStore } from 'react'

import {
  applyThemeToDocument,
  DEFAULT_THEME,
  getStoredTheme,
  setStoredTheme,
  THEME_STORAGE_KEY,
  type Theme,
} from '@/lib/theme/theme-storage'

/**
 * Hook reativo de tema (VEC-195).
 *
 * Usa `useSyncExternalStore` para:
 *   1. Reagir a mudanças em outras abas (evento `storage`).
 *   2. Reagir a `setTheme` local via `CustomEvent('vectraclip:theme-change')`.
 *
 * Retorna `[theme, setTheme]`, similar a `useState`, mas persistido.
 */

const THEME_EVENT = 'vectraclip:theme-change'

function subscribe(callback: () => void): () => void {
  const onStorage = (e: StorageEvent) => {
    if (e.key === THEME_STORAGE_KEY) callback()
  }
  const onCustom = () => callback()

  window.addEventListener('storage', onStorage)
  window.addEventListener(THEME_EVENT, onCustom)
  return () => {
    window.removeEventListener('storage', onStorage)
    window.removeEventListener(THEME_EVENT, onCustom)
  }
}

function getSnapshot(): Theme {
  return getStoredTheme()
}

function getServerSnapshot(): Theme {
  return DEFAULT_THEME
}

export function useTheme(): [Theme, (next: Theme) => void] {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)

  const setTheme = useCallback((next: Theme) => {
    setStoredTheme(next)
    applyThemeToDocument(next)
    // Notifica a própria aba: o evento `storage` só dispara em OUTRAS abas,
    // então precisamos de um custom event para re-renderizar aqui também.
    window.dispatchEvent(new CustomEvent(THEME_EVENT))
  }, [])

  return [theme, setTheme]
}
